import type {
	GuestPageVisibilityMode,
	GuestPageVisibilitySettings,
	GuestPageVisibilitySettingsInput,
} from "@/lib/gql/__generated__/graphql";

/** 施設が切り替えられる公開設定の項目（Plain・Magazine・Snack） */
export type GuestPageVisibilityKey = keyof Pick<
	GuestPageVisibilitySettingsInput,
	"plainVisibility" | "magazineVisibility" | "snackVisibility"
>;

type Props = {
	/** 取得した公開設定 */
	saved: Pick<
		GuestPageVisibilitySettings,
		"id" | "hotelID" | "plainVisibility" | "magazineVisibility" | "snackVisibility"
	>;
	/** 画面で切り替えるページの種類 */
	visibilityKey: GuestPageVisibilityKey;
	/** 画面で切り替えたページの公開状態 */
	visibility: GuestPageVisibilityMode;
};

/**
 * 各画面の保存の mutation に渡す、公開設定の更新の変数を作る
 *
 * 公開設定の更新（guestPageVisibilitySettingsUpdate）は `@include(if: $shouldUpdateGuestPageVisibility)` で、
 * 画面のページの公開状態を変えたときだけ実行する。
 * 変えていないときにも送ると、Google 無料予約リンクの連携先が検索に載らない状態（連携先を「検索に表示しない」にしている・管理者が非公開にしている）の施設では、
 * 検索の設定を触っていなくても画面を保存するたびにエラーになるため。
 *
 * input は 3 ページとも必須なので、画面のページだけ差し替え、ほかのページは取得した値（未設定・非公開を含む）のまま送る。
 */
export const toGuestPageVisibilitySettingsVariables = ({
	saved,
	visibilityKey,
	visibility,
}: Props): {
	guestPageVisibilitySettingsInput: GuestPageVisibilitySettingsInput;
	shouldUpdateGuestPageVisibility: boolean;
} => {
	return {
		guestPageVisibilitySettingsInput: {
			id: saved.id,
			hotelID: saved.hotelID,
			plainVisibility: saved.plainVisibility,
			magazineVisibility: saved.magazineVisibility,
			snackVisibility: saved.snackVisibility,
			[visibilityKey]: visibility,
		},
		shouldUpdateGuestPageVisibility: visibility !== saved[visibilityKey],
	};
};
